import {
  buildCreateEventOption,
  buildMeasureOption,
  buildPointCUASOption,
  buildQrCodeOption,
  CommonContextMenuContext,
  HasPointCUASContext,
} from '@components/map/contextMenu/builders/common';
import { createContextMenuDynamicOptions } from '@components/map/contextMenu/builders/utils';
import { FeatureTypeToContextComponentEnum, MenuBuilder, MenuKey } from '@components/map/contextMenu/type';

import { MapIdEnum, OperationalMapId } from '@/types/map.types';
import { MenuItemOption, MenuItems } from '@/types/menu.types';
import { AutomatonSegment } from '@/types/sensor/sensors';

export type AutomatonSectionContextCore = {
  isSectionInfoDisabled: boolean;
  isCabinetInfoDisabled: boolean;
  isPerimeterDoubtCheckHidden: boolean;
  isPerimeterDoubtCheckDisabled: boolean;
  isRepulsionOff: boolean;
  isRepulsionCommandHidden: boolean;
  isRepulsionCommandLoading: boolean;
  isRepulsionCommandDisabled: boolean;
  isMaintenanceOn: boolean;
  isMaintenanceHidden: boolean;
  isMaintenanceLoading: boolean;
  isMaintenanceDisabled: boolean;
  isSectionConnected: boolean;
  isSectionOptionHidden: boolean;
  isSectionOptionLoading: boolean;
  isDisplayPerimeterCameraOptionHidden: boolean;
  isDisplayPerimeterCameraOptionDisabled: boolean;
  isModifySectionOptionHidden: boolean;
  isHistoryOptionDisabled: boolean;
  segments: AutomatonSegment[];
  modifySectionSubMenuItem: MenuItems;
  handleOpenSectionInfo: () => void;
  handleOpenSectionCabinetInfo: () => void;
  setupForPerimeterDoubtCheckContext: () => void;
  handleSectionRepulsion: (repulsion: boolean) => void;
  handleSectionMaintenance: (segments: AutomatonSegment[]) => void;
  handleSectionActivation: (segments: AutomatonSegment[]) => void;
  openAttachedPerimeterCameras: () => void;
  handlePopup: () => void;
};

export type AutomatonSectionContextMenuContext = CommonContextMenuContext &
  HasPointCUASContext &
  AutomatonSectionContextCore;

export type AutomatonSectionContextMenuStaticOptions = {
  info: boolean;
  cabinetInfo: boolean;
  updateSegment: boolean;
  history: boolean;
};

export type AutomatonSectionContextMenuDynamicOptions = {
  perimeterDoubtCheck: boolean;
  pointCUAS: boolean;
  switchRepulsionMode: boolean;
  switchMaintenanceMode: boolean;
  connection: boolean;
  displayPerimeterCamera: boolean;
  modifySection: boolean;
};

export type AutomatonSectionContextMenuOptions = AutomatonSectionContextMenuStaticOptions &
  AutomatonSectionContextMenuDynamicOptions;

export const AUTOMATON_SECTION_DEFAULT_CONTEXT_MENU_DYNAMIC_OPTIONS: AutomatonSectionContextMenuDynamicOptions = {
  perimeterDoubtCheck: true,
  pointCUAS: true,
  switchRepulsionMode: true,
  switchMaintenanceMode: true,
  connection: true,
  displayPerimeterCamera: true,
  modifySection: true,
};

const allTrue = createContextMenuDynamicOptions(AUTOMATON_SECTION_DEFAULT_CONTEXT_MENU_DYNAMIC_OPTIONS, true);
const allFalse = createContextMenuDynamicOptions(AUTOMATON_SECTION_DEFAULT_CONTEXT_MENU_DYNAMIC_OPTIONS, false);

export const automatonSectionContextMenuConfig: Record<OperationalMapId, AutomatonSectionContextMenuDynamicOptions> = {
  [MapIdEnum.LIVE]: allTrue,
  [MapIdEnum.LAD]: allTrue,
  [MapIdEnum.REPLAY]: allFalse,
  [MapIdEnum.STATISTICS]: allFalse,
};

const buildSectionInfoOption: MenuBuilder<FeatureTypeToContextComponentEnum.AUTOMATON_SECTION>['build'] = (
  ctx,
): [MenuKey, MenuItemOption] => [
  'info',
  {
    label: 'contextmenu.actions.sectionSheet',
    isDisabled: ctx.isSectionInfoDisabled,
    onAction: ctx.handleOpenSectionInfo,
  },
];

const buildCabinetInfoOption: MenuBuilder<FeatureTypeToContextComponentEnum.AUTOMATON_SECTION>['build'] = (
  ctx,
): [MenuKey, MenuItemOption] => [
  'cabinetInfo',
  {
    label: 'contextmenu.actions.cabinetSheet',
    isDisabled: ctx.isCabinetInfoDisabled,
    onAction: ctx.handleOpenSectionCabinetInfo,
  },
];

const buildPerimeterDoubtCheckOption: MenuBuilder<FeatureTypeToContextComponentEnum.AUTOMATON_SECTION>['build'] = (
  ctx,
): [MenuKey, MenuItemOption] => [
  'perimeterDoubtCheck',
  {
    label: 'contextmenu.actions.perimeterDoubtCheck',
    isHidden: ctx.isPerimeterDoubtCheckHidden,
    isDisabled: ctx.isPerimeterDoubtCheckDisabled,
    onAction: ctx.setupForPerimeterDoubtCheckContext,
  },
];

const buildSwitchRepulsionModeOption: MenuBuilder<FeatureTypeToContextComponentEnum.AUTOMATON_SECTION>['build'] = (
  ctx,
): [MenuKey, MenuItemOption] => [
  'switchRepulsionMode',
  {
    label: `contextmenu.actions.modifySection.${ctx.isRepulsionOff ? 'repulsionOn' : 'repulsionOff'}`,
    isHidden: ctx.isRepulsionCommandHidden,
    isLoading: ctx.isRepulsionCommandLoading,
    isDisabled: ctx.isRepulsionCommandDisabled,
    onAction: () => ctx.handleSectionRepulsion(ctx.isRepulsionOff),
  },
];

const buildSwitchMaintenanceModeOption: MenuBuilder<FeatureTypeToContextComponentEnum.AUTOMATON_SECTION>['build'] = (
  ctx,
): [MenuKey, MenuItemOption] => [
  'switchMaintenanceMode',
  {
    label: `contextmenu.actions.${ctx.isMaintenanceOn ? 'maintenanceOff' : 'maintenanceOn'}`,
    isHidden: ctx.isMaintenanceHidden,
    isLoading: ctx.isMaintenanceLoading,
    isDisabled: ctx.isMaintenanceDisabled,
    onAction: () => ctx.handleSectionMaintenance(ctx.segments),
  },
];

const buildConnectionOption: MenuBuilder<FeatureTypeToContextComponentEnum.AUTOMATON_SECTION>['build'] = (
  ctx,
): [MenuKey, MenuItemOption] => [
  'connection',
  {
    label: `contextmenu.actions.${ctx.isSectionConnected ? 'disconnectSection' : 'reconnectSection'}`,
    isHidden: ctx.isSectionOptionHidden,
    isLoading: ctx.isSectionOptionLoading,
    onAction: () => ctx.handleSectionActivation(ctx.segments),
  },
];

const buildDisplayPerimeterCameraOption: MenuBuilder<FeatureTypeToContextComponentEnum.AUTOMATON_SECTION>['build'] = (
  ctx,
): [MenuKey, MenuItemOption] => [
  'displayPerimeterCamera',
  {
    label: 'contextmenu.actions.displayPerimeterCamera',
    isHidden: ctx.isDisplayPerimeterCameraOptionHidden,
    isDisabled: ctx.isDisplayPerimeterCameraOptionDisabled,
    onAction: ctx.openAttachedPerimeterCameras,
  },
];

const buildModifySectionOption: MenuBuilder<FeatureTypeToContextComponentEnum.AUTOMATON_SECTION>['build'] = (
  ctx,
): [MenuKey, MenuItemOption] => [
  'modifySection',
  {
    label: 'contextmenu.actions.modifySection.title',
    isHidden: ctx.isModifySectionOptionHidden,
    subMenuItems: ctx.modifySectionSubMenuItem,
  },
];

const buildHistoryOption: MenuBuilder<FeatureTypeToContextComponentEnum.AUTOMATON_SECTION>['build'] = (
  ctx,
): [MenuKey, MenuItemOption] => [
  'history',
  {
    label: `contextmenu.actions.sectionHistory`,
    isDisabled: ctx.isHistoryOptionDisabled,
    onAction: ctx.handlePopup,
  },
];

export const automatonSectionBuilders: MenuBuilder<FeatureTypeToContextComponentEnum.AUTOMATON_SECTION>[] = [
  {
    order: 1,
    build: buildSectionInfoOption,
  },
  {
    order: 2,
    build: buildCabinetInfoOption,
  },
  {
    order: 3,
    build: buildQrCodeOption,
  },
  {
    order: 4,
    build: buildMeasureOption,
  },
  {
    order: 5,
    build: buildCreateEventOption,
  },
  {
    order: 6,
    separator: true,
  },
  {
    order: 7,
    build: buildPerimeterDoubtCheckOption,
  },
  {
    order: 8,
    build: buildPointCUASOption,
  },
  {
    order: 9,
    build: buildSwitchRepulsionModeOption,
  },
  {
    order: 9,
    build: buildSwitchMaintenanceModeOption,
  },
  {
    order: 10,
    build: buildConnectionOption,
  },
  {
    order: 11,
    build: buildDisplayPerimeterCameraOption,
  },
  {
    order: 12,
    build: buildModifySectionOption,
  },
  {
    order: 13,
    separator: true,
  },
  {
    order: 14,
    build: buildHistoryOption,
  },
];
